import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";

const CONTACT_EMAIL = "privacy@vexamos.com";
const COMPANY = "Vidya Vikas Technocrats Services (OPC) Pvt Ltd.";
const APP_NAME = "Vexamos";
const EFFECTIVE_DATE = "August 14, 2026";
const WEBSITE = "https://www.vexamos.com";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="privacy-page">
      {/* Top bar */}
      <header className="border-b border-zinc-200 sticky top-0 bg-white z-10">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900" data-testid="privacy-back-link">
            <ArrowLeft size={14} weight="bold" /> Home
          </Link>
          <div className="font-display text-lg font-bold tracking-tight">EXAM<span className="text-blue-600">/</span>OS</div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-12 py-12 lg:py-16">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-semibold">Legal</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-3">Privacy Policy</h1>
        <div className="text-sm text-zinc-500 mb-10">
          Effective date: <span className="font-medium text-zinc-900" data-testid="privacy-effective-date">{EFFECTIVE_DATE}</span> ·
          &nbsp;Applies to the {APP_NAME} web and mobile applications.
        </div>

        <div className="prose prose-zinc max-w-none space-y-8">
          <Section title="1. Introduction">
            <p>
              {COMPANY} (&ldquo;{APP_NAME},&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;)
              provides an online examination and certification platform for coaching centers, institutes and their
              learners. This Privacy Policy explains what personal information we collect through our website
              (<a className="link-underline" href={WEBSITE}>{WEBSITE}</a>) and the {APP_NAME} mobile application
              (the &ldquo;App&rdquo;), why we collect it, how we use it, and the choices you have.
            </p>
            <p>
              By creating an account or using {APP_NAME}, you agree to the collection and use of your information
              as described here.
            </p>
          </Section>

          <Section title="2. Information We Collect">
            <SubHeading>a. Information you provide to us</SubHeading>
            <ul>
              <li><strong>Account &amp; profile data:</strong> name, email address, password (stored as a bcrypt hash), role, institution code, profile picture and any avatar you upload.</li>
              <li><strong>Institution data:</strong> if you are an administrator, we store the institution name, logo, certificate branding and the users you add.</li>
              <li><strong>Exam content:</strong> questions, options, correct answers, scoring rules and metadata that examiners upload or generate.</li>
              <li><strong>Exam attempts &amp; answers:</strong> the answers, code submissions, timings and outcomes for each exam attempt.</li>
              <li><strong>Certificates:</strong> we store the exam title, cert ID, score, issue date and (optionally) your uploaded avatar for placement on the certificate PDF.</li>
              <li><strong>Payments:</strong> if you subscribe to a paid plan, our payment processor (e.g. Stripe or Razorpay) collects payment details on our behalf. We only receive a transaction confirmation.</li>
              <li><strong>Support &amp; communications:</strong> messages you send us through help-desk, WhatsApp CTA or the in-app proctor chat.</li>
            </ul>

            <SubHeading>b. Information collected automatically</SubHeading>
            <ul>
              <li><strong>Device &amp; usage data:</strong> app version, device model, OS version, IP address, log events and diagnostics used to run and improve the service.</li>
              <li><strong>Cookies / storage:</strong> a secure session token is stored to keep you signed in. We do not use third-party ad-tracking cookies.</li>
            </ul>

            <SubHeading>c. Camera, microphone and screen data (proctoring)</SubHeading>
            <p>
              For proctored exams the App may request permission to access your <strong>camera and microphone</strong>.
              While the exam is in progress we monitor for policy violations (multiple faces, absence, tab-switch,
              audio spikes) and may capture <strong>periodic snapshots</strong> or short audio-level samples.
              Snapshots are attached to the attempt so your examiner or the assigned proctor can review integrity
              afterwards. Live video streaming to a human proctor is opt-in per exam. Camera / mic access is used
              exclusively for proctoring and never for advertising.
            </p>
          </Section>

          <Section title="3. How We Use Your Information">
            <ul>
              <li>Create and manage your account, authenticate sign-in and enforce email verification (6-digit OTP).</li>
              <li>Deliver exams, evaluate answers, calculate scores and issue signed certificates with a QR verification link.</li>
              <li>Detect and flag exam-integrity violations via AI-assisted proctoring signals.</li>
              <li>Send transactional emails (welcome, exam results, certificate issued, OTP codes) &mdash; we do not send marketing email without your consent.</li>
              <li>Provide analytics dashboards to institution admins and examiners about their own students and exams.</li>
              <li>Improve reliability, prevent abuse, and comply with legal obligations.</li>
            </ul>
          </Section>

          <Section title="4. Legal Basis (GDPR)">
            <p>Where GDPR applies, we rely on: performance of a contract with you (running the exam service), your consent (camera/mic for proctoring, marketing communications you opt into), our legitimate interest (fraud prevention, service improvement) and compliance with law.</p>
          </Section>

          <Section title="5. Sharing With Third Parties">
            <p>We do not sell your personal data. We share limited data with vetted service providers who process it on our behalf:</p>
            <ul>
              <li><strong>Email delivery:</strong> Resend (transactional email including OTP, welcome and certificate notifications).</li>
              <li><strong>Code execution (coding questions):</strong> Judge0 &mdash; only the submitted source code and language ID are sent for compilation and evaluation.</li>
              <li><strong>AI question generation and grading:</strong> OpenAI, Anthropic and Google Gemini (via the Emergent LLM proxy) &mdash; used only when an examiner invokes AI-generate or auto-grade features.</li>
              <li><strong>Object storage:</strong> Emergent Managed Object Storage for uploaded images, attachments and certificate assets.</li>
              <li><strong>Payment processing:</strong> Stripe / Razorpay (only for institutions that subscribe).</li>
              <li><strong>Authentication:</strong> Google Sign-In via the Emergent-managed OAuth service.</li>
              <li><strong>Compliance:</strong> law enforcement or courts when we have a good-faith belief we are legally required.</li>
            </ul>
          </Section>

          <Section title="6. Data Retention">
            <ul>
              <li>Account records: retained while your account is active and up to 12 months after deletion for dispute-resolution.</li>
              <li>Exam attempts &amp; certificates: retained as long as the institution requires them for audit; institutions may configure a shorter retention window.</li>
              <li>Proctoring snapshots &amp; violation logs: purged automatically 90 days after the exam attempt, unless flagged for investigation.</li>
              <li>Session tokens &amp; OTP records: expire in 7 days and 10 minutes respectively.</li>
            </ul>
          </Section>

          <Section title="7. Your Rights">
            <p>You may exercise the following rights at any time by contacting <a className="link-underline" href={`mailto:${CONTACT_EMAIL}`} data-testid="privacy-contact-email">{CONTACT_EMAIL}</a>:</p>
            <ul>
              <li><strong>Access &amp; portability:</strong> request a copy of the personal data we hold about you.</li>
              <li><strong>Correction:</strong> update any inaccurate profile information (or edit it directly from your profile page).</li>
              <li><strong>Deletion:</strong> request permanent deletion of your account and personal data (institution audit records may be retained in anonymised form to preserve exam integrity for other users).</li>
              <li><strong>Withdraw consent:</strong> revoke camera / mic permission from your device settings at any time; note that revoking prevents you from taking proctored exams.</li>
              <li><strong>Objection &amp; restriction:</strong> where applicable, ask us to stop or restrict certain processing.</li>
            </ul>
          </Section>

          <Section title="8. Children">
            <p>{APP_NAME} is designed for learners aged 13 and above. If you are a coaching center or school that
            enrolls students under 13, you (as the institution) are responsible for obtaining verifiable parental
            consent under applicable laws (e.g. COPPA, DPDP Act, GDPR-K). Contact us if you need to delete a
            minor&rsquo;s data.</p>
          </Section>

          <Section title="9. Security">
            <p>We use HTTPS for all traffic, bcrypt for password hashing, HttpOnly + Secure session cookies, rate-limited
            login and OTP endpoints, and role-based access controls. Uploaded files are stored via managed object storage
            with private access-tokens. No system is 100% secure &mdash; report any concern to <a className="link-underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</p>
          </Section>

          <Section title="10. International Transfers">
            <p>Our servers and sub-processors may be located outside your country of residence (including the United
            States and the European Union). When we transfer personal data across borders we use standard contractual
            clauses or equivalent safeguards.</p>
          </Section>

          <Section title="11. Changes to This Policy">
            <p>We may update this policy from time to time. When we do, we will change the &ldquo;Effective date&rdquo;
            above and, for material changes, notify you by email or an in-app banner. Continued use of {APP_NAME} after
            the changes take effect constitutes acceptance of the updated policy.</p>
          </Section>

          <Section title="12. Contact Us">
            <p>
              Questions about this policy or how we handle your data?
              <br />
              Email: <a className="link-underline font-medium text-zinc-900" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
              <br />
              Website: <a className="link-underline font-medium text-zinc-900" href={WEBSITE}>{WEBSITE}</a>
              <br />
              WhatsApp support: <a className="link-underline font-medium text-zinc-900" href="https://wa.me/919154637357" target="_blank" rel="noreferrer">+91 91546 37357</a>
              <br />
              Operator: {COMPANY}
            </p>
          </Section>
        </div>

        <div className="mt-16 border-t border-zinc-200 pt-6 text-xs text-zinc-500 flex items-center justify-between">
          <Link to="/" className="hover:text-zinc-900" data-testid="privacy-home-link">← Back to home</Link>
          <div className="font-mono">v1.0</div>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-2xl font-bold tracking-tight mb-3 mt-2">{title}</h2>
      <div className="text-sm text-zinc-700 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

function SubHeading({ children }) {
  return <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mt-4 mb-1">{children}</div>;
}
